import { deserializeBlob } from '@/lib/audio/utils';
import {
  type TranscriptChunk,
  type TranscriptionResult,
  type TranscriptRecording,
  type TranscriptResponse,
} from '@/lib/SessionRecordingProvider/t11nClient';
import { type SessionChunk } from '@heidi/capture/contracts';
import { formatISO } from 'date-fns';
import { last, round } from 'lodash-es';

// Creates the chunk we would expect the server to return but does it client side.
// Used to optimistically update the query cache.
export const getClientRenderedChunk = (
  { index, duration, recordingId, blob: blobSerialized }: SessionChunk,
  data: TranscriptionResult,
  state: TranscriptResponse,
  recording?: TranscriptRecording
): TranscriptChunk => {
  const blob = deserializeBlob(blobSerialized)!;
  const previousChunk = last(recording?.chunks);
  const previousChunkDuration = previousChunk?.audio_info.duration;

  const timestamp = previousChunkDuration
    ? previousChunk.timestamp_ms + previousChunkDuration
    : 0;

  const previousRecordings = state.recordings.filter(
    (r) => r.recording_id !== recordingId
  );

  const totalDurationFromPreviousRecordings = previousRecordings.reduce((total, rec) => {
    return (
      total +
      rec.chunks.reduce(
        (recTotal, chunk) => recTotal + (chunk.audio_info.duration || 0),
        0
      )
    );
  }, 0);

  const totalDurationFromCurrentRecording =
    recording?.chunks.reduce((total, chunk) => {
      return total + (chunk.audio_info.duration || 0);
    }, 0) || 0;

  const timestampCumulative =
    totalDurationFromPreviousRecordings + totalDurationFromCurrentRecording;

  return {
    text: data.text,
    audio_info: {
      duration: round(duration * 1000),
      size: null,
      mime_type: blob.type,
    },
    confidence: data.confidence ?? 0,
    created_at: formatISO(new Date()),
    index,
    model: data.model,
    timestamp_ms: timestamp,
    timestamp_cumulative_ms: timestampCumulative,
    diarised_transcript: data.diarised_transcript,
  };
};
